import React from "react"

import { useTranslation } from "react-i18next"

import { Grid } from "@mui/material"
import { Typography } from "@synapse-analytics/synapse-ui"
import { useFormikContext } from "formik"

import { ConfusionMatrixChart } from "../../../../components/graphs/HeatMap/ConfusionMatrixChart"
import { EvaluationMetrics } from "../../../../components/tables/EvaluationMetrics/EvaluationMetrics"
import { MAX_SLIDER_VALUE, MIN_SLIDER_VALUE, SINGLE_SLIDER_DEFAULT_VALUE } from "../../Constants"
import type { AutoMLV1Props, ConfigFormikProps } from "../../Interfaces"
import { BaseSlider } from "../../Slider/BaseSlider"

/**
 * AutoMLConfigurationForm is a form component that renders a slider and a table and a confusion matrix.
 * The slider allows users to set a threshold for the model.
 * The table shows the results of the model.
 * The confusion matrix shows the confusion matrix of the model.
 * The component is disabled if the model is not of type "automl".
 * The component is only rendered if the model has a id.
 *
 * @param {AutoMLV1Props} props - The props of the component.
 * @returns {React.ReactElement} - The JSX element to be rendered.
 */
export function AutoMLV1(props: Readonly<AutoMLV1Props>): React.ReactElement {
  const { confusionMatrixData, modelId, disabled, onChangeHandler } = props

  const {
    values: { singleThreshold },
  } = useFormikContext<ConfigFormikProps>()
  const { t } = useTranslation("ModelConfigurations")

  return (
    <Grid container direction="column" item xs={12}>
      <Grid item container direction={"column"}>
        <Grid item mb={2}>
          <Typography variant="label-bold" color="base" variantColor={2}>
            {t("thresholdsTitle")}
          </Typography>
        </Grid>

        <Grid item mb={2} px={1}>
          <BaseSlider
            variant="single"
            sliderProps={{
              value: singleThreshold,
              onChange: onChangeHandler,
              disabled: disabled,
              min: MIN_SLIDER_VALUE,
              max: MAX_SLIDER_VALUE,
              defaultValue: SINGLE_SLIDER_DEFAULT_VALUE,
              marks: [
                { value: MIN_SLIDER_VALUE, label: MIN_SLIDER_VALUE },
                { value: MAX_SLIDER_VALUE, label: MAX_SLIDER_VALUE },
              ],
            }}
          />
        </Grid>
      </Grid>

      <Grid container item>
        <EvaluationMetrics />
        <Grid container spacing={1}>
          {modelId && (
            <Grid item xs={12} lg={12}>
              <ConfusionMatrixChart chartData={confusionMatrixData} />
            </Grid>
          )}
        </Grid>
      </Grid>
    </Grid>
  )
}
